import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import DrawerShell from './ui/DrawerShell'
import EmptyState from './ui/EmptyState'
import ErrorState from './ui/ErrorState'
import FilterBar from './ui/FilterBar'
import LoadingState from './ui/LoadingState'
import StatusBadge from './ui/StatusBadge'
import { formatPlainText, formatTitleText } from './utils/displayText'
import { normalizeStatus, STATUS_CHIP_CLASS } from './utils/statusFormatting'
import { formatDateKey } from './utils/dateFormatting'

const allowedStatuses = ['Pending', 'Scheduled', 'Completed']
const statusChipClassByValue = STATUS_CHIP_CLASS

const defaultCreateJobRequiredColumns = ['site_id', 'job_type', 'job_date']

const buildCreateJobForm = (prefilledDate = '') => ({
  site_id: '',
  job_type: '',
  job_date: prefilledDate || formatDateKey(new Date()),
  status: 'Pending',
  notes: ''
})

const startOfMonth = (value) => new Date(value.getFullYear(), value.getMonth(), 1)

const monthLabel = (value) => value.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })

const buildCalendarCells = (monthDate) => {
  const first = startOfMonth(monthDate)
  const firstDayIndex = first.getDay()
  const start = new Date(first)
  start.setDate(start.getDate() - firstDayIndex)

  const cells = []
  for (let i = 0; i < 42; i += 1) {
    const day = new Date(start)
    day.setDate(start.getDate() + i)
    cells.push(day)
  }
  return cells
}

const isAuthorizationPolicyError = (error) => {
  const code = String(error?.code || '').toLowerCase()
  const message = String(error?.message || '').toLowerCase()
  const details = String(error?.details || '').toLowerCase()
  const hint = String(error?.hint || '').toLowerCase()

  return (
    code === '42501' ||
    message.includes('row-level security') ||
    details.includes('row-level security') ||
    hint.includes('row-level security')
  )
}

const buildAuthorizationPolicyMessage = (actionLabel, target) =>
  `Failed to ${actionLabel} ${target}: authorization context blocked by database policy. Confirm this signed-in profile has manager/shift-lead/admin role with an active worker binding.`

export default function JobsCalendarView({ refreshKey, onJobsChanged }) {
  const [jobs, setJobs] = useState([])
  const [sites, setSites] = useState([])
  const [activeWorkers, setActiveWorkers] = useState([])
  const [selectedWorkerByJob, setSelectedWorkerByJob] = useState({})
  const [editedJobDateById, setEditedJobDateById] = useState({})
  const [editedJobNotesById, setEditedJobNotesById] = useState({})
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [assignmentError, setAssignmentError] = useState('')
  const [statusError, setStatusError] = useState('')
  const [editError, setEditError] = useState('')
  const [savingStatusJobId, setSavingStatusJobId] = useState(null)
  const [savingDateJobId, setSavingDateJobId] = useState(null)
  const [savingNotesJobId, setSavingNotesJobId] = useState(null)
  const [savingAssignmentKey, setSavingAssignmentKey] = useState('')
  const [statusFilter, setStatusFilter] = useState('All')
  const [siteFilter, setSiteFilter] = useState('all')
  const [activeMonthDate, setActiveMonthDate] = useState(() => startOfMonth(new Date()))
  const [selectedDate, setSelectedDate] = useState(() => formatDateKey(new Date()))
  const [detailJobId, setDetailJobId] = useState(null)
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)
  const [createJobForm, setCreateJobForm] = useState(() => buildCreateJobForm(formatDateKey(new Date())))
  const [createSaving, setCreateSaving] = useState(false)
  const [createError, setCreateError] = useState('')
  const [createSuccessMessage, setCreateSuccessMessage] = useState('')
  const [requiredJobColumns, setRequiredJobColumns] = useState(defaultCreateJobRequiredColumns)

  const fetchJobs = useCallback(async () => {
    setLoading(true)
    setErrorMessage('')

    const [
      { data: jobsData, error: jobsError },
      { data: sitesData, error: sitesError },
      { data: workersData, error: workersError },
      { data: assignmentsData, error: assignmentsError }
    ] = await Promise.all([
      supabase.from('jobs').select('id, site_id, job_type, job_date, status, notes'),
      supabase.from('sites').select('id, name'),
      supabase.from('workers').select('id, name, is_active'),
      supabase.from('job_worker_assignments').select('job_id, worker_id')
    ])

    if (jobsError || sitesError || workersError || assignmentsError) {
      setErrorMessage(
        'Unable to load calendar jobs: ' +
          (jobsError?.message || sitesError?.message || workersError?.message || assignmentsError?.message)
      )
      setJobs([])
      setSites([])
      setActiveWorkers([])
      setLoading(false)
      return
    }

    const loadedSites = sitesData || []
    const loadedWorkers = workersData || []
    const loadedAssignments = assignmentsData || []
    const siteNameById = new Map(loadedSites.map((site) => [String(site.id), site.name]))
    const workerById = new Map(loadedWorkers.map((worker) => [String(worker.id), worker]))
    const assignedWorkersByJob = {}

    loadedAssignments.forEach((assignment) => {
      const jobId = String(assignment.job_id)
      const worker = workerById.get(String(assignment.worker_id))
      if (!assignedWorkersByJob[jobId]) assignedWorkersByJob[jobId] = []
      assignedWorkersByJob[jobId].push({
        worker_id: assignment.worker_id,
        name: worker?.name || 'Unknown Worker'
      })
    })

    const mappedJobs = (jobsData || []).map((job) => ({
      ...job,
      site_name: siteNameById.get(String(job.site_id)) || 'Unknown Site',
      assigned_workers: assignedWorkersByJob[String(job.id)] || []
    }))

    setJobs(mappedJobs)
    setSites(loadedSites)
    setActiveWorkers(loadedWorkers.filter((worker) => worker.is_active !== false))

    setSelectedWorkerByJob((prev) => {
      const next = { ...prev }
      mappedJobs.forEach((job) => {
        if (typeof next[job.id] === 'undefined') next[job.id] = ''
      })
      return next
    })

    setEditedJobDateById((prev) => {
      const next = { ...prev }
      mappedJobs.forEach((job) => {
        if (typeof next[job.id] === 'undefined') next[job.id] = job.job_date || ''
      })
      return next
    })

    setEditedJobNotesById((prev) => {
      const next = { ...prev }
      mappedJobs.forEach((job) => {
        if (typeof next[job.id] === 'undefined') next[job.id] = job.notes || ''
      })
      return next
    })

    setLoading(false)
  }, [])

  useEffect(() => {
    fetchJobs()
  }, [refreshKey, fetchJobs])

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const statusMatches = statusFilter === 'All' || normalizeStatus(job.status) === statusFilter
      const siteMatches = siteFilter === 'all' || String(job.site_id) === String(siteFilter)
      return statusMatches && siteMatches
    })
  }, [jobs, statusFilter, siteFilter])

  const jobsByDate = useMemo(() => {
    const map = {}
    filteredJobs.forEach((job) => {
      const key = formatDateKey(job.job_date)
      if (!key) return
      if (!map[key]) map[key] = []
      map[key].push(job)
    })
    return map
  }, [filteredJobs])

  const selectedDayJobs = useMemo(() => jobsByDate[selectedDate] || [], [jobsByDate, selectedDate])
  const detailJob = useMemo(
    () => filteredJobs.find((job) => String(job.id) === String(detailJobId)) || null,
    [filteredJobs, detailJobId]
  )

  const calendarCells = useMemo(() => buildCalendarCells(activeMonthDate), [activeMonthDate])
  const todayKey = formatDateKey(new Date())

  const announceJobsChanged = () => {
    if (onJobsChanged) onJobsChanged()
  }

  const resolveRequiredJobColumns = useCallback(async () => {
    if (typeof supabase.schema !== 'function') {
      setRequiredJobColumns(defaultCreateJobRequiredColumns)
      return defaultCreateJobRequiredColumns
    }

    const { data, error } = await supabase
      .schema('information_schema')
      .from('columns')
      .select('column_name, is_nullable, column_default')
      .eq('table_schema', 'public')
      .eq('table_name', 'jobs')

    if (error || !Array.isArray(data) || data.length === 0) {
      setRequiredJobColumns(defaultCreateJobRequiredColumns)
      return defaultCreateJobRequiredColumns
    }

    const requiredColumns = data
      .filter((column) => String(column.is_nullable || '').toUpperCase() === 'NO' && !column.column_default)
      .map((column) => String(column.column_name || '').trim().toLowerCase())
      .filter(Boolean)
      .filter((column) => !['id', 'created_at', 'updated_at'].includes(column))

    const resolvedColumns = requiredColumns.length > 0 ? requiredColumns : defaultCreateJobRequiredColumns
    setRequiredJobColumns(resolvedColumns)
    return resolvedColumns
  }, [])

  useEffect(() => {
    resolveRequiredJobColumns()
  }, [resolveRequiredJobColumns])

  const openCreateJobDrawer = () => {
    setDetailJobId(null)
    setCreateError('')
    setCreateSuccessMessage('')
    setCreateJobForm((prev) => ({
      ...prev,
      job_date: selectedDate || prev.job_date || formatDateKey(new Date())
    }))
    setCreateDrawerOpen(true)
  }

  const closeCreateJobDrawer = () => {
    setCreateDrawerOpen(false)
    setCreateError('')
    setCreateSaving(false)
  }

  const handleCreateJobFieldChange = (field, value) => {
    setCreateJobForm((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const buildJobCreatePayload = (requiredColumns) => {
    const payload = {}
    const missingFields = []
    const unsupportedRequiredColumns = []

    const normalizedForm = {
      site_id: String(createJobForm.site_id || '').trim(),
      job_type: formatPlainText(createJobForm.job_type || '').trim(),
      job_date: String(createJobForm.job_date || '').trim(),
      status: normalizeStatus(createJobForm.status),
      notes: formatPlainText(createJobForm.notes || '').trim()
    }

    requiredColumns.forEach((column) => {
      if (column === 'site_id') {
        if (!normalizedForm.site_id) missingFields.push('Site')
        payload.site_id = normalizedForm.site_id
        return
      }
      if (column === 'job_type') {
        if (!normalizedForm.job_type) missingFields.push('Job Type')
        payload.job_type = normalizedForm.job_type
        return
      }
      if (column === 'job_date') {
        if (!normalizedForm.job_date) missingFields.push('Job Date')
        payload.job_date = normalizedForm.job_date
        return
      }
      if (column === 'status') {
        if (!normalizedForm.status) missingFields.push('Status')
        payload.status = normalizedForm.status
        return
      }
      if (column === 'notes') {
        if (!normalizedForm.notes) missingFields.push('Notes')
        payload.notes = normalizedForm.notes
        return
      }
      unsupportedRequiredColumns.push(column)
    })

    if (!('site_id' in payload) && normalizedForm.site_id) payload.site_id = normalizedForm.site_id
    if (!('job_type' in payload) && normalizedForm.job_type) payload.job_type = normalizedForm.job_type
    if (!('job_date' in payload) && normalizedForm.job_date) payload.job_date = normalizedForm.job_date
    if (!('status' in payload)) payload.status = normalizedForm.status
    if (!('notes' in payload)) payload.notes = normalizedForm.notes || null

    return {
      payload,
      normalizedForm,
      missingFields,
      unsupportedRequiredColumns
    }
  }

  const handleCreateJob = async (event) => {
    event.preventDefault()
    if (createSaving) return

    setCreateError('')
    setCreateSuccessMessage('')

    const requiredColumns = await resolveRequiredJobColumns()
    const {
      payload,
      normalizedForm,
      missingFields,
      unsupportedRequiredColumns
    } = buildJobCreatePayload(requiredColumns)

    if (unsupportedRequiredColumns.length > 0) {
      setCreateError(
        `Cannot create job because required jobs columns are not supported by this form: ${unsupportedRequiredColumns.join(', ')}.`
      )
      return
    }

    if (missingFields.length > 0) {
      setCreateError(`Job creation requires: ${Array.from(new Set(missingFields)).join(', ')}.`)
      return
    }

    setCreateSaving(true)

    const { data: createdJob, error } = await supabase
      .from('jobs')
      .insert([payload])
      .select('id, site_id, job_type, job_date, status, notes')
      .single()

    if (error) {
      if (isAuthorizationPolicyError(error)) {
        setCreateError(buildAuthorizationPolicyMessage('create', 'job'))
      } else {
        setCreateError(`Failed to create job: ${error.message}`)
      }
      setCreateSaving(false)
      return
    }

    await fetchJobs()
    announceJobsChanged()

    const nextSelectedDate = formatDateKey(createdJob?.job_date || normalizedForm.job_date || selectedDate)
    if (nextSelectedDate) {
      setSelectedDate(nextSelectedDate)
    }
    setCreateJobForm(buildCreateJobForm(nextSelectedDate || formatDateKey(new Date())))
    setCreateSaving(false)
    setCreateDrawerOpen(false)
    setCreateSuccessMessage('Job created successfully.')
  }

  const handleStatusChange = async (jobId, nextStatus) => {
    const normalizedStatus = normalizeStatus(nextStatus)
    setSavingStatusJobId(jobId)
    setStatusError('')

    const { error } = await supabase.from('jobs').update({ status: normalizedStatus }).eq('id', jobId)
    if (error) {
      setStatusError('Failed to update status: ' + error.message)
      setSavingStatusJobId(null)
      return
    }

    setJobs((prev) => prev.map((job) => (String(job.id) === String(jobId) ? { ...job, status: normalizedStatus } : job)))
    announceJobsChanged()
    setSavingStatusJobId(null)
  }

  const handleSaveDate = async (jobId) => {
    const nextDate = editedJobDateById[jobId]
    if (!nextDate) {
      setEditError('Job date is required.')
      return
    }

    setSavingDateJobId(jobId)
    setEditError('')
    const { error } = await supabase.from('jobs').update({ job_date: nextDate }).eq('id', jobId)
    if (error) {
      setEditError('Failed to update date: ' + error.message)
      setSavingDateJobId(null)
      return
    }

    setJobs((prev) => prev.map((job) => (String(job.id) === String(jobId) ? { ...job, job_date: nextDate } : job)))
    announceJobsChanged()
    setSavingDateJobId(null)
  }

  const handleSaveNotes = async (jobId) => {
    const nextNotes = editedJobNotesById[jobId] || ''
    setSavingNotesJobId(jobId)
    setEditError('')
    const { error } = await supabase.from('jobs').update({ notes: nextNotes }).eq('id', jobId)
    if (error) {
      setEditError('Failed to update notes: ' + error.message)
      setSavingNotesJobId(null)
      return
    }

    setJobs((prev) => prev.map((job) => (String(job.id) === String(jobId) ? { ...job, notes: nextNotes } : job)))
    announceJobsChanged()
    setSavingNotesJobId(null)
  }

  const handleAssignWorker = async (jobId) => {
    const selectedWorkerId = selectedWorkerByJob[jobId]
    if (!selectedWorkerId) return

    const targetJob = jobs.find((job) => String(job.id) === String(jobId))
    const alreadyAssigned = (targetJob?.assigned_workers || []).some(
      (assigned) => String(assigned.worker_id) === String(selectedWorkerId)
    )
    if (alreadyAssigned) {
      setAssignmentError('This worker is already assigned to that job.')
      return
    }

    const hasSameDayConflict = jobs.some((job) => {
      if (String(job.id) === String(jobId)) return false
      if (!job.job_date || !targetJob?.job_date) return false
      if (String(job.job_date) !== String(targetJob.job_date)) return false
      return (job.assigned_workers || []).some(
        (assigned) => String(assigned.worker_id) === String(selectedWorkerId)
      )
    })

    if (hasSameDayConflict) {
      setAssignmentError(
        'Worker is already assigned to another job on this date. Remove the same-day assignment before reassigning.'
      )
      return
    }

    const key = `${jobId}:${selectedWorkerId}:assign`
    setSavingAssignmentKey(key)
    setAssignmentError('')

    const { error } = await supabase.from('job_worker_assignments').insert([{ job_id: jobId, worker_id: selectedWorkerId }])
    if (error) {
      if ((error.message || '').toLowerCase().includes('duplicate')) {
        setAssignmentError('This worker is already assigned to that job.')
      } else if (isAuthorizationPolicyError(error)) {
        setAssignmentError(buildAuthorizationPolicyMessage('assign', 'worker'))
      } else {
        setAssignmentError('Failed to assign worker: ' + error.message)
      }
      setSavingAssignmentKey('')
      return
    }

    const worker = activeWorkers.find((row) => String(row.id) === String(selectedWorkerId))
    setJobs((prev) =>
      prev.map((job) => {
        if (String(job.id) !== String(jobId)) return job
        return {
          ...job,
          assigned_workers: [...job.assigned_workers, { worker_id: selectedWorkerId, name: worker?.name || 'Worker' }]
        }
      })
    )
    setSelectedWorkerByJob((prev) => ({ ...prev, [jobId]: '' }))
    announceJobsChanged()
    setSavingAssignmentKey('')
  }

  const handleUnassignWorker = async (jobId, workerId) => {
    const key = `${jobId}:${workerId}:remove`
    setSavingAssignmentKey(key)
    setAssignmentError('')

    const { error } = await supabase
      .from('job_worker_assignments')
      .delete()
      .eq('job_id', jobId)
      .eq('worker_id', workerId)

    if (error) {
      if (isAuthorizationPolicyError(error)) {
        setAssignmentError(buildAuthorizationPolicyMessage('unassign', 'worker'))
      } else {
        setAssignmentError('Failed to unassign worker: ' + error.message)
      }
      setSavingAssignmentKey('')
      return
    }

    setJobs((prev) =>
      prev.map((job) => {
        if (String(job.id) !== String(jobId)) return job
        return {
          ...job,
          assigned_workers: job.assigned_workers.filter(
            (assignedWorker) => String(assignedWorker.worker_id) !== String(workerId)
          )
        }
      })
    )
    announceJobsChanged()
    setSavingAssignmentKey('')
  }

  const onClickToday = () => {
    const today = new Date()
    setActiveMonthDate(startOfMonth(today))
    setSelectedDate(formatDateKey(today))
  }

  if (loading) {
    return <LoadingState title="Loading calendar" message="Preparing schedule board and day view." />
  }

  if (errorMessage) {
    return (
      <ErrorState
        title="Calendar unavailable"
        message={errorMessage}
        action={
          <button type="button" className="gg-button gg-button--secondary" onClick={fetchJobs}>
            Retry
          </button>
        }
      />
    )
  }

  return (
    <section className="gg-tool-surface gg-calendar-surface">
      <div className="gg-tool-header">
        <div>
          <h3 className="gg-tool-title">Calendar Board</h3>
          <p className="gg-tool-subtitle">Date-first planning with quick detail access.</p>
        </div>
      </div>

      {(statusError || assignmentError || editError || createError || createSuccessMessage) && (
        <div className="gg-alert-stack">
          {statusError && <p className="gg-inline-alert gg-inline-alert--error">{statusError}</p>}
          {assignmentError && <p className="gg-inline-alert gg-inline-alert--error">{assignmentError}</p>}
          {editError && <p className="gg-inline-alert gg-inline-alert--error">{editError}</p>}
          {createError && <p className="gg-inline-alert gg-inline-alert--error">{createError}</p>}
          {createSuccessMessage && <p className="gg-inline-alert gg-inline-alert--success">{createSuccessMessage}</p>}
        </div>
      )}

      <div className="gg-calendar-layout">
        <section className="gg-calendar-board">
          <div className="gg-calendar-board__toolbar">
            <div className="gg-form-actions">
              <button
                type="button"
                className="gg-button gg-button--ghost"
                onClick={() => setActiveMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="gg-button gg-button--ghost"
                onClick={() => setActiveMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
              >
                Next
              </button>
              <button type="button" className="gg-button gg-button--secondary" onClick={onClickToday}>
                Today
              </button>
            </div>
            <p className="gg-calendar-board__month">{monthLabel(activeMonthDate)}</p>
          </div>

          <FilterBar
            left={
              <>
                <label htmlFor="calendar-status-filter">Status</label>
                <select
                  id="calendar-status-filter"
                  className="gg-filter-control"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="All">All</option>
                  {allowedStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <label htmlFor="calendar-site-filter">Site</label>
                <select
                  id="calendar-site-filter"
                  className="gg-filter-control"
                  value={siteFilter}
                  onChange={(e) => setSiteFilter(e.target.value)}
                >
                  <option value="all">All Sites</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {formatTitleText(site.name, 'Unnamed Site')}
                    </option>
                  ))}
                </select>
              </>
            }
            summary={`${filteredJobs.length} scheduled jobs`}
            right={
              <button type="button" className="gg-button gg-button--ghost" onClick={fetchJobs}>
                Refresh
              </button>
            }
          />

          <div className="gg-calendar-grid">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
              <div key={label} className="gg-calendar-day-label">
                {label}
              </div>
            ))}
            {calendarCells.map((day) => {
              const key = formatDateKey(day)
              const dayJobs = jobsByDate[key] || []
              const inMonth = day.getMonth() === activeMonthDate.getMonth()
              const isSelected = key === selectedDate
              const isToday = key === todayKey
              return (
                <div
                  key={key}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedDate(key)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      setSelectedDate(key)
                    }
                  }}
                  aria-label={`Open schedule for ${day.toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}`}
                  className={[
                    'gg-calendar-day',
                    inMonth ? '' : 'gg-calendar-day--out',
                    isSelected ? 'gg-calendar-day--selected' : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="gg-calendar-day__top">
                    <span className={`gg-calendar-day__date ${isToday ? 'gg-calendar-day__date--today' : ''}`}>
                      {day.getDate()}
                    </span>
                    {dayJobs.length > 0 && <span className="gg-calendar-day__count">{dayJobs.length}</span>}
                  </div>
                  {dayJobs.slice(0, 3).map((job) => {
                    const status = normalizeStatus(job.status)
                    return (
                      <button
                        key={job.id}
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          setDetailJobId(job.id)
                          setSelectedDate(key)
                        }}
                        className={statusChipClassByValue[status] || 'gg-calendar-chip gg-calendar-chip--default'}
                      >
                        {formatTitleText(job.job_type, 'Job')} • {formatTitleText(job.site_name, 'Unknown Site')}
                        {job.assigned_workers.length > 0 ? ` (${job.assigned_workers.length})` : ''}
                      </button>
                    )
                  })}
                  {dayJobs.length > 3 && <div className="gg-calendar-day__more">+{dayJobs.length - 3} more</div>}
                </div>
              )
            })}
          </div>
        </section>

        <aside className="gg-calendar-day-panel">
          <h3>Jobs for {selectedDate || 'Selected Day'}</h3>
          <div className="gg-form-actions">
            <button
              type="button"
              className="gg-button gg-button--primary"
              onClick={openCreateJobDrawer}
            >
              Create Job on Selected Date
            </button>
          </div>

          {selectedDayJobs.length === 0 ? (
            <EmptyState title="No jobs on this day" message="Select another date or create a new job here." />
          ) : (
            <div className="gg-calendar-day-jobs">
              {selectedDayJobs.map((job) => (
                <article key={job.id} className="gg-calendar-day-job">
                  <p className="gg-calendar-day-job__title">
                    {formatTitleText(job.job_type, 'Job')} • {formatTitleText(job.site_name, 'Unknown Site')}
                  </p>
                  <p>
                    <StatusBadge status={normalizeStatus(job.status)} />
                  </p>
                  <p>Assigned: {job.assigned_workers.length}</p>
                  <div className="gg-form-actions">
                    <button
                      type="button"
                      className="gg-button gg-button--secondary"
                      onClick={() => setDetailJobId(job.id)}
                    >
                      Open Job Detail
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </aside>
      </div>

      <DrawerShell
        open={createDrawerOpen}
        title="Create Job"
        subtitle={selectedDate ? `Selected date ${selectedDate}` : 'Create a new job'}
        onClose={closeCreateJobDrawer}
      >
        <form className="gg-drawer-form" onSubmit={handleCreateJob}>
          <label className="gg-form-field" htmlFor="create-job-site">
            <span>Site *</span>
            <select
              id="create-job-site"
              className="gg-select"
              value={createJobForm.site_id}
              onChange={(event) => handleCreateJobFieldChange('site_id', event.target.value)}
              disabled={createSaving}
            >
              <option value="">Select site</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {formatTitleText(site.name, 'Unnamed Site')}
                </option>
              ))}
            </select>
          </label>

          <label className="gg-form-field" htmlFor="create-job-type">
            <span>Job Type *</span>
            <input
              id="create-job-type"
              className="gg-input"
              value={createJobForm.job_type}
              onChange={(event) => handleCreateJobFieldChange('job_type', event.target.value)}
              onBlur={(event) => handleCreateJobFieldChange('job_type', formatPlainText(event.target.value))}
              placeholder="Harvest"
              disabled={createSaving}
            />
          </label>

          <label className="gg-form-field" htmlFor="create-job-date">
            <span>Job Date *</span>
            <input
              id="create-job-date"
              className="gg-input"
              type="date"
              value={createJobForm.job_date}
              onChange={(event) => handleCreateJobFieldChange('job_date', event.target.value)}
              disabled={createSaving}
            />
          </label>

          <label className="gg-form-field" htmlFor="create-job-status">
            <span>Status</span>
            <select
              id="create-job-status"
              className="gg-select"
              value={createJobForm.status}
              onChange={(event) => handleCreateJobFieldChange('status', event.target.value)}
              disabled={createSaving}
            >
              {allowedStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>

          <label className="gg-form-field" htmlFor="create-job-notes">
            <span>Notes</span>
            <textarea
              id="create-job-notes"
              className="gg-textarea"
              value={createJobForm.notes}
              onChange={(event) => handleCreateJobFieldChange('notes', event.target.value)}
              onBlur={(event) => handleCreateJobFieldChange('notes', formatPlainText(event.target.value))}
              disabled={createSaving}
            />
          </label>

          {requiredJobColumns.length > 0 ? (
            <p className="gg-text-soft">Required DB columns detected: {requiredJobColumns.join(', ')}</p>
          ) : null}

          <div className="gg-form-actions">
            <button type="submit" className="gg-button gg-button--primary" disabled={createSaving}>
              {createSaving ? 'Creating...' : 'Create Job'}
            </button>
            <button type="button" className="gg-button gg-button--secondary" onClick={closeCreateJobDrawer} disabled={createSaving}>
              Cancel
            </button>
          </div>
        </form>
      </DrawerShell>

      <DrawerShell
        open={Boolean(detailJob)}
        title={detailJob ? `${formatTitleText(detailJob.job_type, 'Job')} Detail` : 'Job Detail'}
        subtitle={detailJob ? formatTitleText(detailJob.site_name, 'Unknown Site') : ''}
        onClose={() => setDetailJobId(null)}
      >
        {detailJob ? (
          <div className="gg-drawer-form">
            <div className="gg-drawer-meta">
              <p><strong>Site:</strong> {formatTitleText(detailJob.site_name, 'Unknown Site')}</p>
              <p><strong>Date:</strong> {detailJob.job_date || 'N/A'}</p>
            </div>

            <div className="gg-form-field">
              <span>Status</span>
              <div className="gg-job-card__inline">
                <select
                  className="gg-select"
                  value={normalizeStatus(detailJob.status)}
                  onChange={(e) => handleStatusChange(detailJob.id, e.target.value)}
                  disabled={savingStatusJobId === detailJob.id}
                >
                  {allowedStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                {savingStatusJobId === detailJob.id && <span className="gg-text-soft">Saving...</span>}
              </div>
            </div>

            <div className="gg-form-field">
              <span>Date</span>
              <div className="gg-job-card__inline">
                <input
                  className="gg-input"
                  type="date"
                  value={editedJobDateById[detailJob.id] || ''}
                  onChange={(e) =>
                    setEditedJobDateById((prev) => ({
                      ...prev,
                      [detailJob.id]: e.target.value
                    }))
                  }
                  disabled={savingDateJobId === detailJob.id}
                />
                <button
                  type="button"
                  className="gg-button gg-button--secondary"
                  onClick={() => handleSaveDate(detailJob.id)}
                  disabled={savingDateJobId === detailJob.id}
                >
                  {savingDateJobId === detailJob.id ? 'Saving...' : 'Save Date'}
                </button>
              </div>
            </div>

            <label className="gg-form-field">
              <span>Notes</span>
              <textarea
                className="gg-textarea"
                value={editedJobNotesById[detailJob.id] || ''}
                onChange={(e) =>
                  setEditedJobNotesById((prev) => ({
                    ...prev,
                    [detailJob.id]: e.target.value
                  }))
                }
                onBlur={(e) =>
                  setEditedJobNotesById((prev) => ({
                    ...prev,
                    [detailJob.id]: formatPlainText(e.target.value)
                  }))
                }
                disabled={savingNotesJobId === detailJob.id}
              />
            </label>
            <div className="gg-form-actions">
              <button
                type="button"
                className="gg-button gg-button--secondary"
                onClick={() => handleSaveNotes(detailJob.id)}
                disabled={savingNotesJobId === detailJob.id}
              >
                {savingNotesJobId === detailJob.id ? 'Saving...' : 'Save Notes'}
              </button>
            </div>

            <div className="gg-form-field">
              <span>Assign Worker</span>
              <div className="gg-job-card__inline">
                <select
                  className="gg-select"
                  value={selectedWorkerByJob[detailJob.id] || ''}
                  onChange={(e) =>
                    setSelectedWorkerByJob((prev) => ({
                      ...prev,
                      [detailJob.id]: e.target.value
                    }))
                  }
                  disabled={savingAssignmentKey.startsWith(`${detailJob.id}:`)}
                >
                  <option value="">Select active worker</option>
                  {activeWorkers
                    .filter(
                      (worker) =>
                        !(detailJob.assigned_workers || []).some(
                          (assignedWorker) => String(assignedWorker.worker_id) === String(worker.id)
                        )
                    )
                    .map((worker) => (
                      <option key={worker.id} value={worker.id}>
                        {formatTitleText(worker.name, 'Unnamed Worker')}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  className="gg-button gg-button--secondary"
                  onClick={() => handleAssignWorker(detailJob.id)}
                  disabled={!selectedWorkerByJob[detailJob.id] || savingAssignmentKey.startsWith(`${detailJob.id}:`)}
                >
                  {savingAssignmentKey.endsWith(':assign') && savingAssignmentKey.startsWith(`${detailJob.id}:`)
                    ? 'Assigning...'
                    : 'Assign'}
                </button>
              </div>
            </div>

            <div className="gg-form-field">
              <span>Assigned Workers</span>
              {detailJob.assigned_workers.length === 0 ? (
                <p className="gg-text-soft">No workers assigned.</p>
              ) : (
                <div className="gg-drawer-assignment-list">
                  {detailJob.assigned_workers.map((assignedWorker) => {
                    const removeKey = `${detailJob.id}:${assignedWorker.worker_id}:remove`
                    return (
                      <div key={`${detailJob.id}:${assignedWorker.worker_id}`} className="gg-assignment-item">
                        <span>{formatTitleText(assignedWorker.name, 'Worker')}</span>
                        <button
                          type="button"
                          className="gg-button gg-button--ghost"
                          onClick={() => handleUnassignWorker(detailJob.id, assignedWorker.worker_id)}
                          disabled={savingAssignmentKey === removeKey}
                        >
                          {savingAssignmentKey === removeKey ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </DrawerShell>
    </section>
  )
}
